import React from "react";
import { Grid, IconButton, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";
import { HighlightOffOutlined as HighlightOffOutlinedIcon } from "@mui/icons-material";

import { Contact } from "Models/contact";
import { Timezone } from "Models/timezone";
import { Input, PhoneInput, Select } from "Components/Form";
import { ContactType } from "Util/enums";
import { phoneNumberIsValid } from "Util/formatPhoneNumber";
import { AppTimezoneSelect } from "Components/Generic/AppTimezoneSelect";

interface ContactCardProps {
  contact: Contact;
  index?: number;
  timezones?: Timezone[];
  viewOnly?: boolean;
  showActions?: boolean;
  isModal?: boolean;
  errors?: { [key: string]: string };
  onFieldChange: (field: string, value: any) => void;
  onEdit?: (contact: Contact, index: number) => void;
  onDelete?: (contact: Contact, index: number) => void;
  classes?: any;
}

export const ContactCard: React.FC<ContactCardProps> = ({
  contact,
  index = 0,
  timezones,
  viewOnly = false,
  showActions,
  errors = {},
  onFieldChange,
  onEdit,
  onDelete,
  classes = {},
}) => {
  const { t: translate } = useTranslation();

  const fieldHasError = (field: string) => !!errors[field];
  const getErrorByField = (field: string) => errors[field];

  const isPrimaryContact = contact?.isPrimary || contact?.contactType === ContactType.PRIMARY;

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<any>) => {
    const value = event.target.value;
    onFieldChange(field, value);

    // Se está mudando o tipo de contato para Primary, também define isPrimary
    if (field === 'contactType' && value === ContactType.PRIMARY) {
      onFieldChange('isPrimary', true);
    }
  };

  const handlePhoneChange = (field: string) => (value: string) => {
    if (phoneNumberIsValid(value)) {
      onFieldChange(field, field === 'phone' ? (value || null) : value);
    }
  };

  const handleTimezoneChange = (timezone?: Timezone) => {
    onFieldChange('timezone', timezone);
    onFieldChange('timezoneId', timezone?.id);
  };

  const renderDeleteButton = () => {
    if (!isPrimaryContact && showActions && !viewOnly && onDelete) {
      return (
        <IconButton
          disabled={viewOnly}
          className={classes.iconButton}
          onClick={() => onDelete(contact, index)}
        >
          <HighlightOffOutlinedIcon className={classes.icon} htmlColor="#F97066" />
        </IconButton>
      );
    }
    return null;
  };

  const renderEditButton = () => {
    return null;
    if (showActions && !viewOnly && onEdit) {
      return (
        <IconButton
          disabled={viewOnly}
          className={classes.iconButton}
          onClick={() => onEdit?.(contact, index)}
        >
          <FiEdit className={classes.icon} color="#98A2B3" />
        </IconButton>
      );
    }
    return null;
  };

  const renderTimezoneField = () => {
    const timezone = timezones?.find((tz) => tz.id === contact?.timezoneId);

    // Na lista, usar AppTimezoneSelect como no AgentDetailsTab
    return (
      <AppTimezoneSelect
        handleChange={handleTimezoneChange}
        value={timezone}
        isReadonly={viewOnly}
        hasShowAutoUpdateTimezoneByCurrentUserBroswerLabel={false}
      />
    );
  };

  return (
    <div className={classes.contacts}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <Input
            label={translate("firstName")}
            onChange={(event) => onFieldChange('firstName', event.target.value)}
            placeholder={translate("enterFirstName")}
            required
            value={contact?.firstName}
            disabled={viewOnly}
            error={fieldHasError("firstName")}
            errorMessage={getErrorByField("firstName")}
            data-cy="contact-first-name"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Input
            label={translate("lastName")}
            onChange={(event) => onFieldChange('lastName', event.target.value)}
            placeholder={translate("enterLastName")}
            required
            value={contact?.lastName}
            disabled={viewOnly}
            error={fieldHasError("lastName")}
            errorMessage={getErrorByField("lastName")}
            data-cy="contact-last-name"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Input
            label={translate("email")}
            onChange={(event) => onFieldChange('email', event.target.value)}
            placeholder={"example@gmail.com"}
            required
            value={contact?.email}
            disabled={viewOnly}
            error={fieldHasError("email")}
            errorMessage={getErrorByField("email")}
            data-cy="contact-email"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Select
            name="agent.contactType"
            label={translate("contactType")}
            onChange={handleSelectChange('contactType')}
            value={contact?.contactType}
            error={fieldHasError("contactType")}
            errorMessage={getErrorByField("contactType")}
            required
            disabled={isPrimaryContact || viewOnly}
            data-cy="contactType"
          >
            {!isPrimaryContact
              ? [{ value: ContactType.SECONDARY, text: translate("secondary") }, { value: ContactType.BILLING, text: translate("billing") }, { value: ContactType.OPERATIONS, text: translate("operations") }].map(item => {
                return (
                  <MenuItem value={item.value} key={item.value}>
                    {item.text}
                  </MenuItem>
                );
              })
              : <MenuItem value={ContactType.PRIMARY}>{translate("primary")}</MenuItem>}
          </Select>
        </Grid>

        <Grid item xs={12} sm={3}>
          <PhoneInput
            label={translate("mobileNumber")}
            placeholder={translate("enterHere")}
            onChange={handlePhoneChange('mobilePhone')}
            value={contact?.mobilePhone}
            disabled={viewOnly}
            error={fieldHasError("mobile")}
            errorMessage={getErrorByField("mobile")}
            data-cy="contact-mobile"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <PhoneInput
            label={translate("phoneNumber")}
            placeholder={translate("enterHere")}
            onChange={handlePhoneChange('phone')}
            value={contact?.phone}
            disabled={viewOnly}
            data-cy="contact-phone"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          {renderTimezoneField()}
        </Grid>

        {showActions && (
          <Grid item>
            {renderEditButton()}
            {renderDeleteButton()}
          </Grid>
        )}
      </Grid>
    </div>
  );
};
